<template>
  <div class="turno" :class="{ anulado: estado === 'anulado', bloqueo: esBloqueo }">

    <!-- IZQUIERDA: hora -->
    <div class="hora-bloque">
      <span class="hora-inicio">{{ inicio }}</span>
      <span class="hora-fin">→ {{ fin }}</span>
    </div>

    <!-- CENTRO: paciente + motivo -->
    <div class="info">
      <span class="nombre" :class="{ 'no-link': esBloqueo }" @click.stop="irAPaciente">{{ paciente }}</span>
      <span class="motivo">{{ motivo }}</span>
    </div>

    <!-- DERECHA: badge + dropdown -->
    <div class="badge-wrapper">
      <!-- Bloqueo/descanso: badge gris fijo, sin dropdown de estado (no es una cita). -->
      <button
        v-if="esBloqueo"
        class="badge-estado"
        :style="{ background: colorEstado, cursor: 'default' }"
        @click.stop
      >
        Bloqueo
      </button>
      <button
        v-else
        ref="badgeRef"
        class="badge-estado"
        :style="{ background: colorEstado }"
        @click.stop="toggleDropdown"
      >
        {{ labelEstado }}
      </button>
      <div v-if="dropdownAbierto" class="dropdown-estados" :style="{ top: dropdownPos.top + 'px', right: dropdownPos.right + 'px' }">
        <button
          v-for="op in ESTADOS"
          :key="op.valor"
          class="dropdown-item"
          :style="{ color: op.color }"
          @click.stop="seleccionar(op.valor)"
        >
          {{ op.label }}
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";

const props = defineProps({
  inicio:     String,
  fin:        String,
  paciente:   String,
  motivo:     String,
  pacienteId: Number,
  estado:     String,
  tipo:       String,
  id:         [Number, String],
});

const esBloqueo = computed(() => props.tipo === 'bloqueo');

const emit = defineEmits(['cambiar-estado']);

const router = useRouter();
const dropdownAbierto = ref(false);
const dropdownPos    = ref({ top: 0, right: 0 });
const badgeRef       = ref(null);

const ESTADOS = [
  { valor: 'pendiente',  label: 'No confirmado', color: '#678AB2' },
  { valor: 'confirmado', label: 'Confirmado',     color: '#22c55e' },
  { valor: 'en curso',   label: 'En curso',       color: '#f59e0b' },
  { valor: 'atendido',   label: 'Atendido',       color: '#a855f7' },
  { valor: 'no asiste',  label: 'No asiste',      color: '#92400e' },
  { valor: 'anulado',    label: 'Anulado',        color: '#ef4444' },
];

const COLORES = {
  'pendiente':  '#678AB2',
  'confirmado': '#22c55e',
  'en curso':   '#f59e0b',
  'atendido':   '#a855f7',
  'no asiste':  '#92400e',
  'anulado':    '#ef4444',
};

const colorEstado = computed(() => esBloqueo.value ? '#9ca3af' : (COLORES[props.estado] || '#678AB2'));
const labelEstado = computed(() => {
  const op = ESTADOS.find(e => e.valor === props.estado);
  return op ? op.label : 'No confirmado';
});

function toggleDropdown() {
  const wasOpen = dropdownAbierto.value;
  document.dispatchEvent(new CustomEvent('close-all-dropdowns'));
  if (!wasOpen) {
    const rect = badgeRef.value.getBoundingClientRect();
    dropdownPos.value = {
      top:   rect.bottom + 6,
      right: window.innerWidth - rect.right,
    };
    dropdownAbierto.value = true;
  }
}

function seleccionar(valor) {
  emit('cambiar-estado', valor);
  dropdownAbierto.value = false;
}

function irAPaciente() {
  if (esBloqueo.value || !props.pacienteId) return;
  router.push(`/paciente/${props.pacienteId}`);
}

function cerrarDropdown() {
  dropdownAbierto.value = false;
}

onMounted(() => {
  document.addEventListener('click', cerrarDropdown);
  document.addEventListener('close-all-dropdowns', cerrarDropdown);
  window.addEventListener('scroll', cerrarDropdown, true);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', cerrarDropdown);
  document.removeEventListener('close-all-dropdowns', cerrarDropdown);
  window.removeEventListener('scroll', cerrarDropdown, true);
});
</script>

<style scoped>
.turno {
  background: #fff;
  border-radius: 16px;
  padding: 12px 16px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  width: 100%;
  display: grid;
  grid-template-columns: 70px 1fr auto;
  align-items: center;
  gap: 10px;
  position: relative;
}

.turno.anulado {
  opacity: 0.55;
}

/* Bloqueo / descanso: fondo grisáceo para diferenciarlo de una cita real. */
.turno.bloqueo {
  background: #eceef1;
  box-shadow: none;
}

.turno:hover {
  box-shadow: 0 4px 12px rgba(103, 138, 178, 0.35);
}

/* IZQUIERDA */
.hora-bloque {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hora-inicio {
  font-size: 15px;
  font-weight: 700;
  color: #678AB2;
}

.hora-fin {
  font-size: 12px;
  color: #9ca3af;
}

/* CENTRO */
.info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.nombre {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
  cursor: pointer;
}

.nombre:hover {
  text-decoration: underline;
}

.nombre.no-link {
  cursor: default;
}

.nombre.no-link:hover {
  text-decoration: none;
}

.motivo {
  font-size: 13px;
  color: #555;
}

/* DERECHA: badge + dropdown */
.badge-wrapper {
  position: relative;
}

.badge-estado {
  border: none;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  transition: filter 0.15s;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.badge-estado:hover {
  filter: brightness(0.88);
}

.dropdown-estados {
  position: fixed;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.12);
  z-index: 1000;
  min-width: 148px;
  overflow: hidden;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  text-align: left;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.dropdown-item:hover {
  background: #f3f4f6;
}

/* Tablet */
@media (min-width: 768px) {
  .turno       { padding: 14px 18px; }
  .hora-inicio { font-size: 16px; }
  .nombre      { font-size: 16px; }
  .motivo      { font-size: 14px; }
}

/* Desktop */
@media (min-width: 1024px) {
  .turno       { padding: 16px 20px; }
  .hora-inicio { font-size: 17px; }
  .nombre      { font-size: 17px; }
}
</style>
